/**
 * CF-PRIVACY-PAGE-V1: /privacy page content.
 *
 * Mirrors the lib/terms-content.ts pattern (version stamp + last-updated +
 * a markdown string rendered by the bounded mini-parser in pages/Privacy.tsx).
 * Display-only; there is no acceptance gate, so PRIVACY_VERSION is just a stamp.
 *
 * Copy approved by PL #7461/#7462 (2026-06-02). Facts are kept consistent with
 * the live Terms "Your data" section (terms-content.ts v4-2026-06-01) and the
 * Terms governing-law/contact (Costa Rica operator; support@cricketflip.com).
 * No new legal claims beyond the Terms.
 *
 * Syntax kept to what the Privacy.tsx parser supports: `#`/`##` headings,
 * `- ` list items, blank lines as paragraph breaks. No bold/italic/HTML.
 */

export const PRIVACY_VERSION = 'v1-2026-06-02';
export const PRIVACY_LAST_UPDATED = '2026-06-02';

export const PRIVACY_TEXT = `# CricketFlip — Privacy Policy

Last updated: 2026-06-02

## 1. Introduction

CricketFlip ("CricketFlip", "we", "us") operates a provably-fair coin-toss game played with Toncoin (TON) inside Telegram. This Privacy Policy explains what information we collect, why we collect it, how we use and protect it, and the choices you have. It applies to the CricketFlip Telegram Mini App and the website at cricketflip.com. The service is operated by the CricketFlip operator, incorporated in Costa Rica.

We collect only what we need to run the game. We do not sell your data.

## 2. Information We Collect

We collect the following categories of information:
- Telegram account identifiers — your Telegram user ID and username, provided by Telegram when you open the app, used to identify your account and balance.
- TON wallet address — the public address of the wallet you connect and bind, so we can credit deposits and send withdrawals.
- On-chain transaction data — the transaction hashes of your deposits and withdrawals, which are already public and permanent on the TON blockchain.
- Gameplay and account records — your flips (stake, choice, outcome, and signed flip identifier), your in-game balance, and your deposit and withdrawal history.
- Technical and security logs — anonymous logs such as errors, timing, and abuse signals, used for diagnostics and fraud prevention.
- Information stored on your device — a small amount of data in your device's local storage (your onboarding progress and your flip receipts), which remains on your device.

We do not collect your name, email address, telephone number, physical location, or payment-card details. We never ask for your wallet's secret keys or seed phrase, and you should never share those with anyone.

## 3. How We Use Information

We use your information solely to operate and protect the game: to authenticate you, to hold and reconcile your balance, to process deposits and withdrawals, to allow you to verify the fairness of each flip, to prevent fraud and abuse, to diagnose and fix problems, and to comply with applicable legal obligations.

## 4. How We Share Information

We do not sell your personal data and we do not share it for marketing or advertising. We share information only as follows:
- Telegram — as the platform the app runs on, subject to Telegram's own privacy policy.
- The TON blockchain — deposit and withdrawal transactions are recorded on a public ledger by their nature; this is inherent to using a blockchain and is not within our control.
- Service providers — infrastructure and hosting providers that process data on our behalf to run the service.
- Legal and safety — where we are required to by a valid legal request, or where reasonably necessary to prevent fraud or protect our rights or our users.

## 5. Analytics and Cookies

We use privacy-friendly, cookie-free analytics to measure aggregate usage such as app opens. These analytics do not track you across other websites and do not build an advertising profile.

## 6. Data Retention

We retain your account, balance, and game records for as long as your account is active and for as long as needed for accounting, fraud-prevention, and legal purposes. On-chain data (deposits, withdrawals, and transaction hashes) is public and permanent by the nature of the blockchain, and cannot be changed or deleted by us.

## 7. Data Security

We protect your information using technical and organisational measures, including encrypted connections (HTTPS), access controls, monitoring, and manual review of unusual withdrawals. No method of transmission or storage is completely secure, and we cannot guarantee absolute security; you are responsible for safeguarding your own wallet keys and Telegram account.

## 8. Your Rights and Choices

- You may withdraw your full balance to your bound wallet at any time, and stop playing whenever you choose.
- You may clear the data stored on your device (onboarding progress and receipts) by clearing the app's storage in Telegram.
- To ask about the information we hold about you, or to request access or deletion where applicable, contact us using the details below. Records we are required to keep for legal, accounting, or fraud-prevention reasons, and data already public on the blockchain, may not be deletable.

## 9. International Processing

CricketFlip is operated from Costa Rica, and your information may be processed and stored on servers operated by us and our service providers that may be located in other countries. Where information is transferred across borders, we take reasonable steps to protect it as described in this policy.

## 10. Children

CricketFlip is intended only for adults — you must be at least 18 years old, or the legal age for online gaming where you live. The service is not directed to children, and we do not knowingly collect information from them.

## 11. Changes and Contact

We may update this Privacy Policy from time to time. The current version always applies and is available here in the app. Material changes will be reflected by the "Last updated" date above.

Questions about this policy or your data: support@cricketflip.com, or @cricketflip_official on Telegram.
`;
